import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  IPlanBenefitSummaryList,
  IPlanOptionsSummary,
  IPlanSummary,
  ITermConditionSummary,
  ITokenReplacement,
  ITripCancellationOptionSummary,
} from '@nibtravel/shared/util/models';
import { ProductsApiEndpointService } from '@nibtravel/shared/util/url';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  PlanBenefitSummaryListMap,
  PlanSummaryMap,
} from './model/plans-state-model';

@Injectable({ providedIn: 'root' })
export class PlanDataService {
  constructor(
    private http: HttpClient,
    private endpointService: ProductsApiEndpointService
  ) {}

  public loadPlansData(
    brand: string,
    planIds: number[]
  ): Observable<PlanSummaryMap> {
    // Get array of plan summaries map and merge as single map object
    return combineLatest(planIds.map((id) => this.loadPlan(id, brand))).pipe(
      map((data) => data.reduce((a, b) => ({ ...a, ...b })))
    );
  }

  public loadPlanBenefitsData(
    brand: string,
    planIds: number[]
  ): Observable<PlanBenefitSummaryListMap> {
    return combineLatest(
      planIds.map((id) => this.loadPlanBenefits(id, brand))
    ).pipe(map((data) => data.reduce((a, b) => ({ ...a, ...b }))));
  }

  private loadPlan(
    planId: number,
    brandCode: string
  ): Observable<PlanSummaryMap> {
    const url = this.endpointService.getPlanSummaryUrl(brandCode, planId);
    return this.http.get<IPlanSummary>(url).pipe(
      map((data) => this.replaceTCTokens(data)),
      map((data) => this.updatePDSLink(data)),
      map((plan) => ({ [plan.planId]: plan }))
    );
  }

  private loadPlanBenefits(
    planId: number,
    brandCode: string
  ): Observable<PlanBenefitSummaryListMap> {
    const url = this.endpointService.getPlanBenefitsUrl(brandCode, planId);
    return this.http
      .get<IPlanBenefitSummaryList>(url)
      .pipe(map((plan) => ({ [plan.planId]: plan })));
  }

  /**
   *
   * @param planId number
   * @param brandCode string
   * @returns Observable<IPlanOptionsSummary>
   * @description Fetch the plan options using PlanId
   */
  public loadPlanOptions(
    planId: number,
    brandCode: string
  ): Observable<Record<number, IPlanOptionsSummary>> {
    const url = this.endpointService.getPlanOptionsUrl(brandCode, planId);
    return this.http.get<IPlanOptionsSummary>(url).pipe(
      map((options) => {
        this.transformOptions(options);
        return { [planId]: options };
      })
    );
  }

  private transformOptions(options: IPlanOptionsSummary): void {
    this.setOptIn(options);
    this.sortTripCancellation(options.cancellationOption);
  }

  private sortTripCancellation(
    options: ITripCancellationOptionSummary | undefined
  ) {
    if (options) {
      // Check whether the options has unlimited
      options.cancellationCoverLimits.forEach((limit) => {
        if (
          limit.isCancellationCoverUnlimited &&
          !limit.cancellationCoverLimit
        ) {
          // Not sure this is always the case, but the API seems to provide that
          limit.cancellationCoverLimit = +limit.name;
        }
      });

      options.cancellationCoverLimits = options.cancellationCoverLimits.sort(
        (a, b) => a.cancellationCoverLimit - b.cancellationCoverLimit
      );
    }
  }

  private setOptIn(options: IPlanOptionsSummary): void {
    if (!options || !options.basicOptions) {
      return;
    }
    for (const basicOption of options.basicOptions) {
      if (basicOption.optOutValue) {
        basicOption.optOutValue.optIn = false;
      }
      basicOption.optInValues.forEach((optInValue) => {
        optInValue.optIn = true;
      });
    }
  }

  private replaceTCTokens(plSummary: IPlanSummary): IPlanSummary {
    const tandc: ITermConditionSummary[] = [
      ...plSummary.purchaseTermsAndConditions,
    ];
    const updatedTandC: ITermConditionSummary[] = tandc.map((tc) => {
      const customerDeclarationText = tc.tokenReplacements.reduce((acc, tr) => {
        const replaceText = this.getReplaceText(tr);
        return replaceText ? acc.replace(tr.token, replaceText) : acc;
      }, tc.customerDeclarationText);

      return {
        ...tc,
        customerDeclarationText,
      };
    });

    return {
      ...plSummary,
      purchaseTermsAndConditions: updatedTandC,
    };
  }

  private getReplaceText(tr: ITokenReplacement): string {
    let replacementText = '';
    const productsUrl = this.endpointService.getApiUrl();
    if (tr.url) {
      const url = (
        /v\d\//.test(tr.url) ? productsUrl + tr.url : tr.url
      ).replace(/(\/v\d){2,}/, '$1');
      replacementText = `<a href="${url}" target="_blank">${tr.label}</a>`;
    } else if (tr.underwriterContentType) {
      replacementText = `<a class="link"
          data-modal="${tr.underwriterContentType}" data-modal-header="${tr.label}">${tr.label}</a>`;
    }
    return replacementText;
  }

  private updatePDSLink(plSummary: IPlanSummary): IPlanSummary {
    const pdsLink = plSummary.pdsUrl;
    const productsUrl = this.endpointService.getApiUrl();
    const updatedUrl = /v\d\//.test(pdsLink)
      ? `${productsUrl}/${pdsLink}`
      : pdsLink;
    return {
      ...plSummary,
      pdsUrl: updatedUrl,
    };
  }
}
